import { Children, ReactNode, useEffect, useRef, useState } from 'react'
import { AlertTriangle, Inbox, Lock } from 'lucide-react'
import Spinner from '@/common/components/Spinner'
import { cn } from '@/common/utils/cn'
import CableConnectionTag from '../CableConnectionTag'

type Props = {
  title: string
  count: number
  children: ReactNode
  accentLeft?: boolean
  className?: string
  refreshSeconds?: number
  onRefresh?: () => void
  realtime?: boolean
  isLoading?: boolean
  isError?: boolean
  forbidden?: boolean
}

const RefreshCountdown = ({ seconds, onRefresh }: { seconds: number; onRefresh?: () => void }) => {
  const [remaining, setRemaining] = useState(seconds)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  useEffect(() => {
    setRemaining(seconds)
    const interval = setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          onRefreshRef.current?.()
          return seconds
        }
        return value - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [seconds])

  return <span className="text-[10px] font-normal text-white-300 shrink-0">Atualiza em {remaining}s</span>
}

const ColumnMessage = ({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle?: string }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-10 text-center">
    <span className="text-white-300">{icon}</span>
    <p className="text-xs font-semibold text-white-400">{title}</p>
    {subtitle && <p className="text-[11px] text-white-300">{subtitle}</p>}
  </div>
)

const AuctionKanbanColumn = ({
  title,
  count,
  children,
  accentLeft = false,
  className,
  refreshSeconds,
  onRefresh,
  realtime,
  isLoading,
  isError,
  forbidden,
}: Props) => {
  const isEmpty = Children.count(children) === 0

  return (
    <div
      className={cn(
        'flex flex-1 min-w-[280px] border border-border-default rounded-lg overflow-hidden',
        className,
      )}
    >
      {accentLeft && <div className="w-[3px] bg-primary-blue shrink-0" />}

      <div className="flex flex-col flex-1 min-w-0">
        <div className="px-4 py-3 bg-white-100 border-b border-border-default shrink-0 flex items-center justify-between gap-2">
          <p className="font-bold text-sm">
            {title}{' '}
            <span className="text-yellow-400">({count})</span>
          </p>
          {refreshSeconds && <RefreshCountdown seconds={refreshSeconds} onRefresh={onRefresh} />}
          {realtime && <CableConnectionTag />}
        </div>

        <div className="flex flex-col gap-3 p-3 overflow-y-auto flex-1 no-scrollbar bg-black-300">
          {forbidden ? (
            <ColumnMessage
              icon={<Lock size={28} strokeWidth={1.5} />}
              title="Sem permissão"
              subtitle="Você não tem permissão para ver estes leilões."
            />
          ) : isError ? (
            <ColumnMessage
              icon={<AlertTriangle size={28} strokeWidth={1.5} />}
              title="Erro ao carregar"
              subtitle="Não foi possível carregar os leilões."
            />
          ) : isLoading && isEmpty ? (
            <div className="flex flex-1 items-center justify-center py-10">
              <Spinner className="h-6 w-6 fill-blue-800" />
            </div>
          ) : isEmpty ? (
            <ColumnMessage
              icon={<Inbox size={28} strokeWidth={1.5} />}
              title="Nenhum leilão"
              subtitle="Nada por aqui no momento."
            />
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  )
}

export default AuctionKanbanColumn
